import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MDButton from "components/MDButton";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment";
import EventResetsService, {
  EventResetBatchDetail,
  EventResetBatchSummary,
  EventResetsApi,
} from "services/eventResetsService";
import orgDataService from "services/orgDataService";
import { Client } from "common/models";
import {
  autoApproveText,
  batchOutcome,
  defaultFilters,
  doneCount,
  EventResetFilters,
  filtersError,
  isActive,
  POLL_MS,
  shouldPoll,
  toBatchQuery,
  tripStatusColor,
} from "./eventResets";
import { responseErrorText } from "./bulkEventReset";

const fmt = (iso?: string | null) => (iso ? moment(iso).format("MM/DD/YYYY HH:mm") : "-");

const duration = (b: EventResetBatchSummary) => {
  if (!b.startedAt) return "-";
  const end = b.finishedAt ? moment(b.finishedAt) : moment();
  const secs = Math.max(0, end.diff(moment(b.startedAt), "seconds"));
  return secs < 90 ? `${secs}s` : `${Math.round(secs / 60)} min`;
};

const BatchTrips: React.FC<{ detail: EventResetBatchDetail | null; error: string | null }> = ({ detail, error }) => {
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detail) return <CircularProgress size={20} sx={{ m: 1 }} />;
  return (
    <Table size="small" data-testid={`batch-trips-${detail.batch.batchId}`}>
      <TableHead>
        <TableRow>
          <TableCell>Trip #</TableCell>
          <TableCell>Client</TableCell>
          <TableCell>Pickup</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Auto-approve</TableCell>
          <TableCell>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {detail.trips.map((t) => (
          <TableRow key={t.tripId} data-testid={`batch-trip-${t.tripId}`}>
            <TableCell>
              <Link to={`/trips/${t.tripId}`} target="_blank" rel="noopener noreferrer">
                {t.tripId}
              </Link>
            </TableCell>
            <TableCell>{t.clientName ?? "-"}</TableCell>
            <TableCell>{t.pickupDateTime ? moment(t.pickupDateTime).format("MM/DD/YYYY HH:mm") : "-"}</TableCell>
            <TableCell>
              <Chip size="small" label={t.status} color={tripStatusColor(t.status)} />
            </TableCell>
            <TableCell>{autoApproveText(t.autoApproved, t.autoApproveFailReason)}</TableCell>
            <TableCell sx={{ maxWidth: 520 }}>{t.reason ?? ""}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const BatchRow: React.FC<{
  batch: EventResetBatchSummary;
  open: boolean;
  onToggle: () => void;
  detail: EventResetBatchDetail | null;
  detailError: string | null;
}> = ({ batch, open, onToggle, detail, detailError }) => {
  const outcome = batchOutcome(batch);
  const done = doneCount(batch);
  return (
    <>
      <TableRow hover sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }} onClick={onToggle} data-testid={`batch-${batch.batchId}`}>
        <TableCell>
          <IconButton size="small" aria-label={open ? "Hide trips" : "Show trips"}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{fmt(batch.requestedAt)}</TableCell>
        <TableCell>{batch.requestedBy ?? batch.requestedByUserId}</TableCell>
        <TableCell>{batch.source}</TableCell>
        <TableCell sx={{ minWidth: 190 }}>
          <Chip size="small" label={outcome.label} color={outcome.color} />
          {isActive(batch) && (
            <Box sx={{ mt: 0.5 }}>
              <LinearProgress variant="determinate" value={batch.tripCount ? (100 * done) / batch.tripCount : 0} />
              <Typography variant="caption">
                {done} of {batch.tripCount} done
              </Typography>
            </Box>
          )}
        </TableCell>
        <TableCell align="right">{batch.tripCount}</TableCell>
        <TableCell align="right">{batch.succeededCount}</TableCell>
        <TableCell align="right">{batch.approvedCount}</TableCell>
        <TableCell align="right">{batch.skippedCount}</TableCell>
        <TableCell align="right">{batch.failedCount}</TableCell>
        <TableCell>{duration(batch)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ my: 1 }}>
              <BatchTrips detail={detail} error={detailError} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

interface Props {
  // Injectable for tests.
  service?: EventResetsApi;
  loadClients?: () => Promise<Client[]>;
}

// Module-level so the default is the same function every render; an inline default re-ran the
// client load effect on every setClients.
const loadAllClients = () => orgDataService.getAllClients();

// Admin-only history of bulk Event Resets (manual from the review queue, later the scheduled run):
// what ran, who ran it, whether it is still running, and how each trip came out. An Admin-only tab on
// the review queue page (ReviewQueuePage); OrderApi enforces Admin on the data as well.
const EventResetsTab: React.FC<Props> = ({ service = EventResetsService, loadClients = loadAllClients }) => {
  const [searchParams] = useSearchParams();
  const focusBatch = searchParams.get("batch");

  const [filters, setFilters] = useState<EventResetFilters>(defaultFilters());
  const [applied, setApplied] = useState<EventResetFilters>(filters);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [batches, setBatches] = useState<EventResetBatchSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>(focusBatch ? { [focusBatch]: true } : {});
  const [details, setDetails] = useState<Record<string, EventResetBatchDetail>>({});
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});
  const [focused, setFocused] = useState<EventResetBatchSummary | null>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    loadClients().then(setClients).catch(() => setClients([]));
  }, [loadClients]);

  const loadDetail = useCallback(
    async (batchId: string) => {
      try {
        const d = await service.getBatch(batchId);
        setDetails((prev) => ({ ...prev, [batchId]: d }));
        setDetailErrors((prev) => {
          const { [batchId]: _, ...rest } = prev;
          return rest;
        });
        return d;
      } catch (e: any) {
        setDetailErrors((prev) => ({ ...prev, [batchId]: responseErrorText(e) || "Could not load this batch." }));
        return null;
      }
    },
    [service]
  );

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const result = await service.getBatches(toBatchQuery(applied, page * pageSize, pageSize));
        setBatches(result.results ?? []);
        setTotal(result.count ?? 0);
        setError(null);
        // Keep open running batches' trip lists live too.
        await Promise.all(
          (result.results ?? []).filter((b) => openRef.current[b.batchId] && isActive(b)).map((b) => loadDetail(b.batchId))
        );
      } catch (e: any) {
        setError(responseErrorText(e) || "Could not load the Event Reset history.");
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [service, applied, page, pageSize, loadDetail]
  );

  useEffect(() => {
    load();
  }, [load]);

  // A batch linked from the review queue's results dialog may sit outside the date range: show it on its own.
  useEffect(() => {
    if (!focusBatch) return;
    loadDetail(focusBatch).then((d) => d && setFocused(d.batch));
  }, [focusBatch, loadDetail]);

  const visible = useMemo(() => (focused && !batches.some((b) => b.batchId === focused.batchId) ? [focused, ...batches] : batches), [focused, batches]);

  // Poll every 5 s only while a running batch is on screen.
  useEffect(() => {
    if (!shouldPoll(visible)) return;
    const timer = setInterval(() => {
      load(true);
      if (focused && isActive(focused)) loadDetail(focused.batchId).then((d) => d && setFocused(d.batch));
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [visible, load, loadDetail, focused]);

  const toggle = (batchId: string) => {
    const next = !open[batchId];
    setOpen((prev) => ({ ...prev, [batchId]: next }));
    if (next && !details[batchId]) loadDetail(batchId);
  };

  const filterProblem = filtersError(filters);
  const search = () => {
    if (filterProblem) return;
    setPage(0);
    setApplied(filters);
  };

  const users = useMemo(() => {
    const seen = new Map<number, string>();
    batches.forEach((b) => seen.set(b.requestedByUserId, b.requestedBy ?? String(b.requestedByUserId)));
    if (applied.userId && !seen.has(Number(applied.userId))) seen.set(Number(applied.userId), applied.userId);
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [batches, applied.userId]);

  const selectSx = { minWidth: 150 };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4">Event Resets</Typography>
        {/* Same look as the review queue's record count. */}
        <Typography variant="body2" sx={{ color: "#8B4513", fontWeight: 500, whiteSpace: "nowrap" }} data-testid="event-resets-count">
          {loading ? "Loading…" : `${total.toLocaleString()} batch${total === 1 ? "" : "es"}`}
        </Typography>
      </Stack>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <DatePicker
              label="From"
              value={filters.from}
              onChange={(v) => setFilters({ ...filters, from: v ? moment(v) : null })}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label="To"
              value={filters.to}
              onChange={(v) => setFilters({ ...filters, to: v ? moment(v) : null })}
              slotProps={{ textField: { size: "small" } }}
            />
            <FormControl size="small" sx={selectSx}>
              <Select displayEmpty value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} inputProps={{ "aria-label": "Status" }}>
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="Running">Running</MenuItem>
                <MenuItem value="Queued">Queued</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={selectSx}>
              <Select displayEmpty value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} inputProps={{ "aria-label": "Source" }}>
                <MenuItem value="">Manual and scheduled</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={selectSx}>
              <Select displayEmpty value={filters.userId} onChange={(e) => setFilters({ ...filters, userId: e.target.value })} inputProps={{ "aria-label": "User" }}>
                <MenuItem value="">All users</MenuItem>
                {users.map(([id, name]) => (
                  <MenuItem key={id} value={String(id)}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={selectSx}>
              <Select displayEmpty value={filters.clientId} onChange={(e) => setFilters({ ...filters, clientId: e.target.value })} inputProps={{ "aria-label": "Client" }}>
                <MenuItem value="">All clients</MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Trip #"
              value={filters.tripId}
              onChange={(e) => setFilters({ ...filters, tripId: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && search()}
              sx={{ width: 130 }}
            />
            <MDButton variant="contained" color="info" size="small" onClick={search} disabled={filterProblem !== null}>
              Search
            </MDButton>
          </Stack>
          {filterProblem && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {filterProblem}
            </Typography>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Requested</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Trips</TableCell>
              <TableCell align="right">Succeeded</TableCell>
              <TableCell align="right">Approved</TableCell>
              <TableCell align="right">Skipped</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((b) => (
              <BatchRow
                key={b.batchId}
                batch={b}
                open={!!open[b.batchId]}
                onToggle={() => toggle(b.batchId)}
                detail={details[b.batchId] ?? null}
                detailError={detailErrors[b.batchId] ?? null}
              />
            ))}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="textSecondary">
                    No Event Resets in this range.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>
    </Box>
  );
};

export default EventResetsTab;
